import * as THREE from 'three';
import { ANGELA_BEHAVIOR_MODES, AngelaBehaviorModeController } from './angel-behavior-mode.js';
import { LipSyncController } from './lip-sync-controller.js';
import { VoiceChat } from './voice-chat.js';

const CONTROL_KEYS = Object.freeze({
  FOLLOW: 'l',
  STAY: 'k',
});

export function isTextInputLikeElement(element) {
  if (!element) return false;
  const tagName = (element.tagName || '').toUpperCase();
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || element.isContentEditable === true;
}

const DEFAULT_SYSTEM_PROMPT = `You are Angela, a warm protective guardian angel in Sean's metaverse heaven called Veridian.
Keep responses concise (1-3 short paragraphs). Be kind, clear, and practical.`;

// ─── Palette + feature detection (mirrors avatar-creator.js logic) ───
const COMPANION_PALETTES = {
  warm:      { primary: 0xFFD700, secondary: 0xFF8C00, accent: 0xFF6347, skin: 0xE8B87A, glow: 0xFFD700, emissive: 0x332200 },
  cool:      { primary: 0x4169E1, secondary: 0x1E90FF, accent: 0x00CED1, skin: 0xB0C4DE, glow: 0x4169E1, emissive: 0x001133 },
  mystical:  { primary: 0x9370DB, secondary: 0x8A2BE2, accent: 0xBA55D3, skin: 0xD8BFD8, glow: 0x9370DB, emissive: 0x220033 },
  shadow:    { primary: 0x2F4F4F, secondary: 0x708090, accent: 0xFF1493, skin: 0x696969, glow: 0xFF1493, emissive: 0x110011 },
  nature:    { primary: 0x228B22, secondary: 0x32CD32, accent: 0xFFD700, skin: 0x8FBC8F, glow: 0x32CD32, emissive: 0x002200 },
  tech:      { primary: 0x00FF00, secondary: 0x0099FF, accent: 0xFF00FF, skin: 0x333344, glow: 0x00FFFF, emissive: 0x001122 },
  celestial: { primary: 0x00BFFF, secondary: 0x4169E1, accent: 0xFFB6C1, skin: 0xF0F8FF, glow: 0x87CEEB, emissive: 0x112244 },
  infernal:  { primary: 0xFF4500, secondary: 0x8B0000, accent: 0xFF6600, skin: 0x8B0000, glow: 0xFF4500, emissive: 0x330000 },
};

function detectMoodAndFeatures(desc) {
  const t = (desc || '').toLowerCase();
  let mood = 'warm'; // default angel
  if (t.match(/cool|ice|frost|blue|water|ocean/)) mood = 'cool';
  else if (t.match(/shadow|dark|night|noir|stealth/)) mood = 'shadow';
  else if (t.match(/nature|forest|earth|green|druid/)) mood = 'nature';
  else if (t.match(/tech|cyber|digital|neon|robot|mech/)) mood = 'tech';
  else if (t.match(/celestial|cosmic|star|space|galaxy/)) mood = 'celestial';
  else if (t.match(/infernal|demon|hell|devil|fire/)) mood = 'infernal';
  else if (t.match(/mystic|magic|wizard|witch|sorcerer/)) mood = 'mystical';

  return {
    mood,
    hasWings:  !t || !!t.match(/wing|fly|angel|bird|bat|dragon/) || !t.match(/no.?wing/),
    wingStyle: t.match(/bat|demon|dragon/) ? 'bat' : t.match(/energy|cosmic|tech/) ? 'energy' : 'feathered',
    hasHalo:   !t || !!t.match(/halo|divine|holy|angel|radiant/) || !t.match(/no.?halo/),
    hasArmor:  !!t.match(/armor|armour|metal|steel|plate|knight|warrior|guardian/),
    hasHorns:  !!t.match(/horn|demon|devil/),
    hasCrown:  !!t.match(/crown|king|queen|royal/),
    bodyType:  t.match(/large|big|heavy|tank/) ? 'heavy' : t.match(/slim|thin|lithe/) ? 'slim' : 'medium',
  };
}

export class AngelCompanion {
  constructor(scene, camera, opts = {}) {
    this.scene = scene;
    this.camera = camera;
    this.followDistance = opts.followDistance ?? 2.8;
    this.followHeight = opts.followHeight ?? 0.2;
    this.botName = opts.botName || 'Angela';
    this.systemPrompt = opts.systemPrompt || DEFAULT_SYSTEM_PROMPT;
    this.avatarUrl = opts.avatarUrl || null;
    this.avatarDescription = opts.avatarDescription || '';
    this.group = this._createModel();
    this.scene.add(this.group);

    // If we have a DALL-E avatar image, load it as a textured billboard in front of the procedural model
    if (this.avatarUrl && this.avatarUrl.startsWith('data:')) {
      this._loadAvatarImage(this.avatarUrl);
    }

    this.behaviorController = opts.behaviorController || new AngelaBehaviorModeController();

    this.lastTalkAt = 0;
    this.voiceReady = false;
    this.synth = window.speechSynthesis || null;
    this.lipSyncController = new LipSyncController();
    this._lastUpdateTimeSec = null;

    this._createUI();
    this._pickVoice();
  }

  _createModel() {
    const { mood, hasWings, wingStyle, hasHalo, hasArmor, hasHorns, hasCrown, bodyType } = detectMoodAndFeatures(this.avatarDescription);
    const pal = COMPANION_PALETTES[mood] || COMPANION_PALETTES.warm;
    const scale = bodyType === 'heavy' ? 1.15 : bodyType === 'slim' ? 0.85 : 1.0;

    const root = new THREE.Group();
    root.name = 'AngelaCompanion';

    // ── Material helpers ──
    const makeMat = (color, opts = {}) => new THREE.MeshStandardMaterial({
      color, roughness: opts.roughness ?? 0.4, metalness: opts.metalness ?? 0.3,
      emissive: opts.emissive ?? new THREE.Color(pal.emissive), emissiveIntensity: opts.emissiveIntensity ?? 0.15,
      ...opts,
    });

    const skinMat = makeMat(pal.skin, { roughness: 0.6, metalness: 0.1 });
    const primaryMat = makeMat(pal.primary, { metalness: 0.6, roughness: 0.2 });
    const accentMat = makeMat(pal.accent, { metalness: 0.5 });

    // ── TORSO ──
    const torsoMat = hasArmor ? primaryMat : makeMat(pal.secondary, { roughness: 0.5 });
    const upperTorso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.14 * scale, 0.2 * scale, 0.45, 12),
      torsoMat
    );
    upperTorso.position.y = 0.95;
    upperTorso.castShadow = true;
    root.add(upperTorso);

    const lowerTorso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2 * scale, 0.14 * scale, 0.3, 12),
      torsoMat
    );
    lowerTorso.position.y = 0.55;
    root.add(lowerTorso);

    // Armor chest plate
    if (hasArmor) {
      const armorMat = makeMat(pal.primary, { metalness: 0.85, roughness: 0.1, emissiveIntensity: 0.3 });
      const chestPlate = new THREE.Mesh(
        new THREE.BoxGeometry(0.3 * scale, 0.25, 0.08),
        armorMat
      );
      chestPlate.position.set(0, 0.95, 0.12);
      root.add(chestPlate);

      // Shoulder pads
      for (const side of [-1, 1]) {
        const pad = new THREE.Mesh(
          new THREE.SphereGeometry(0.1, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2),
          armorMat
        );
        pad.position.set(side * 0.35 * scale, 1.15, 0);
        pad.scale.set(1.2, 1, 1.2);
        root.add(pad);
      }

      // Gem on chest
      const gemMat = new THREE.MeshStandardMaterial({
        color: pal.accent, emissive: new THREE.Color(pal.accent),
        emissiveIntensity: 1.0, roughness: 0.0, metalness: 0.9,
      });
      const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.035, 0), gemMat);
      gem.position.set(0, 1.0, 0.18);
      gem.name = 'companionGem';
      root.add(gem);
    }

    // ── NECK ──
    const neck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.08, 0.1, 10),
      skinMat
    );
    neck.position.y = 1.25;
    root.add(neck);

    // ── HEAD ──
    this.head = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 24, 24),
      skinMat
    );
    this.head.position.y = 1.45;
    this.head.castShadow = true;
    root.add(this.head);

    // Eyes - glowing
    const eyeMat = new THREE.MeshStandardMaterial({
      color: 0x111111, emissive: new THREE.Color(pal.glow),
      emissiveIntensity: 0.8, roughness: 0.1, metalness: 0.8,
    });
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 12, 12), eyeMat);
      eye.position.set(side * 0.07, 1.48, 0.17);
      eye.scale.set(1, 1.2, 0.6);
      root.add(eye);

      // Glow dot
      const glowDot = new THREE.Mesh(
        new THREE.SphereGeometry(0.015, 6, 6),
        new THREE.MeshBasicMaterial({ color: pal.glow })
      );
      glowDot.position.set(side * 0.07, 1.49, 0.20);
      root.add(glowDot);
    }

    // Mouth
    this.mouth = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.015, 0.02),
      new THREE.MeshStandardMaterial({ color: 0x9e4b5a })
    );
    this.mouth.position.set(0, 1.38, 0.18);
    root.add(this.mouth);

    // Horns
    if (hasHorns) {
      const hornMat = makeMat(0x444444, { metalness: 0.7, roughness: 0.3 });
      for (const side of [-1, 1]) {
        const horn = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.22, 8), hornMat);
        horn.position.set(side * 0.12, 1.62, 0);
        horn.rotation.z = side * 0.3;
        root.add(horn);
      }
    }

    // Crown
    if (hasCrown) {
      const crownMat = makeMat(0xFFD700, { metalness: 0.9, roughness: 0.1, emissiveIntensity: 0.4, emissive: new THREE.Color(0x443300) });
      const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.19, 0.08, 6), crownMat);
      crown.position.set(0, 1.65, 0);
      root.add(crown);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const pt = new THREE.Mesh(new THREE.ConeGeometry(0.02, 0.06, 4), crownMat);
        pt.position.set(Math.cos(a) * 0.15, 1.72, Math.sin(a) * 0.15);
        root.add(pt);
      }
    }

    // ── ARMS ──
    const armMat = hasArmor ? primaryMat : skinMat;
    for (const side of [-1, 1]) {
      const arm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.05, 0.4, 8),
        armMat
      );
      arm.position.set(side * (0.32 * scale), 0.9, 0);
      arm.rotation.z = side * 0.12;
      root.add(arm);

      const hand = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 8), skinMat);
      hand.position.set(side * (0.35 * scale), 0.66, 0);
      root.add(hand);
    }

    // ── LEGS ──
    const legMat = hasArmor ? makeMat(pal.secondary, { metalness: 0.5 }) : makeMat(0x333344);
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.04, 0.4, 8),
        legMat
      );
      leg.position.set(side * 0.08, 0.18, 0);
      root.add(leg);

      const boot = new THREE.Mesh(
        new THREE.BoxGeometry(0.07, 0.06, 0.12),
        hasArmor ? primaryMat : makeMat(0x222233)
      );
      boot.position.set(side * 0.08, -0.05, 0.01);
      root.add(boot);
    }

    // ── HALO ──
    if (hasHalo) {
      this.halo = new THREE.Mesh(
        new THREE.TorusGeometry(0.22, 0.018, 10, 36),
        new THREE.MeshStandardMaterial({
          color: 0xffe48e, emissive: 0xffd76a, emissiveIntensity: 0.75
        })
      );
      this.halo.position.y = 1.72;
      this.halo.rotation.x = Math.PI / 2;
      root.add(this.halo);
    } else {
      this.halo = null;
    }

    // ── WINGS ──
    const wingColor = new THREE.Color(pal.primary);
    const wingEmissive = new THREE.Color(pal.glow);
    this.leftWing = null;
    this.rightWing = null;

    if (hasWings) {
      if (wingStyle === 'feathered') {
        const wingMat = new THREE.MeshStandardMaterial({
          color: wingColor, emissive: wingEmissive, emissiveIntensity: 0.15,
          transparent: true, opacity: 0.92, side: THREE.DoubleSide,
        });

        // Build shaped wings with feather layers
        for (const side of [-1, 1]) {
          const wingGroup = new THREE.Group();

          // Main membrane
          const shape = new THREE.Shape();
          shape.moveTo(0, 0);
          shape.quadraticCurveTo(side * 0.3, 0.45, side * 0.65, 0.15);
          shape.quadraticCurveTo(side * 0.5, -0.2, side * 0.2, -0.3);
          shape.lineTo(0, -0.08);
          shape.closePath();
          const membrane = new THREE.Mesh(new THREE.ShapeGeometry(shape), wingMat);
          wingGroup.add(membrane);

          // Feather strips
          for (let f = 0; f < 5; f++) {
            const t = f / 4;
            const feather = new THREE.Mesh(
              new THREE.PlaneGeometry(0.04, 0.15 + (1 - t) * 0.15),
              wingMat
            );
            feather.position.set(side * (0.1 + t * 0.45), 0.15 - t * 0.25, 0.01);
            feather.rotation.z = side * (t * 0.6);
            wingGroup.add(feather);
          }

          wingGroup.position.set(0, 1.05, -0.08);
          root.add(wingGroup);

          if (side === -1) this.leftWing = wingGroup;
          else this.rightWing = wingGroup;
        }
      } else if (wingStyle === 'bat') {
        const batMat = new THREE.MeshStandardMaterial({
          color: pal.secondary, emissive: new THREE.Color(pal.emissive), emissiveIntensity: 0.2,
          side: THREE.DoubleSide,
        });

        for (const side of [-1, 1]) {
          const shape = new THREE.Shape();
          shape.moveTo(0, 0);
          shape.lineTo(side * 0.2, 0.4);
          shape.lineTo(side * 0.5, 0.35);
          shape.lineTo(side * 0.7, 0.05);
          shape.lineTo(side * 0.55, -0.15);
          shape.lineTo(side * 0.25, -0.25);
          shape.lineTo(0, -0.1);
          shape.closePath();
          const wing = new THREE.Mesh(new THREE.ShapeGeometry(shape), batMat);
          wing.position.set(0, 1.05, -0.08);
          root.add(wing);
          if (side === -1) this.leftWing = wing;
          else this.rightWing = wing;
        }
      } else {
        // Energy wings
        const energyMat = new THREE.MeshBasicMaterial({
          color: pal.glow, transparent: true, opacity: 0.3, side: THREE.DoubleSide,
        });
        for (const side of [-1, 1]) {
          const shape = new THREE.Shape();
          shape.moveTo(0, 0);
          shape.quadraticCurveTo(side * 0.3, 0.5, side * 0.6, 0.2);
          shape.quadraticCurveTo(side * 0.4, -0.08, side * 0.15, -0.25);
          shape.lineTo(0, -0.07);
          shape.closePath();
          const wing = new THREE.Mesh(new THREE.ShapeGeometry(shape), energyMat);
          wing.position.set(0, 1.05, -0.08);
          root.add(wing);
          if (side === -1) this.leftWing = wing;
          else this.rightWing = wing;
        }
      }
    }

    // ── AURA PARTICLES ──
    const particleCount = 80;
    const pPositions = new Float32Array(particleCount * 3);
    const pColors = new Float32Array(particleCount * 3);
    const gc = new THREE.Color(pal.glow);
    const ac = new THREE.Color(pal.accent);
    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const r = 0.3 + Math.random() * 0.8;
      pPositions[i * 3] = Math.cos(angle) * r;
      pPositions[i * 3 + 1] = Math.random() * 2;
      pPositions[i * 3 + 2] = Math.sin(angle) * r;
      const c = new THREE.Color().lerpColors(gc, ac, Math.random());
      pColors[i * 3] = c.r;
      pColors[i * 3 + 1] = c.g;
      pColors[i * 3 + 2] = c.b;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPositions, 3));
    pGeo.setAttribute('color', new THREE.BufferAttribute(pColors, 3));
    const pMat = new THREE.PointsMaterial({
      size: 0.03, vertexColors: true, transparent: true, opacity: 0.5,
      blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    });
    this.particles = new THREE.Points(pGeo, pMat);
    this.particles.name = 'companionAura';
    root.add(this.particles);

    // Ambient glow light colored to palette
    const light = new THREE.PointLight(pal.glow, 0.75, 7.5, 2);
    light.position.set(0, 1.2, 0.2);
    root.add(light);

    root.position.set(0, 0, 2.6);
    return root;
  }

  _loadAvatarImage(dataUrl) {
    const loader = new THREE.TextureLoader();
    loader.load(dataUrl, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;

      // Create a rounded billboard panel for the avatar portrait
      const aspect = 1; // DALL-E images are square
      const height = 2.2;
      const width = height * aspect;

      // Main avatar plane
      const geo = new THREE.PlaneGeometry(width, height);
      const mat = new THREE.MeshStandardMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide,
        emissive: new THREE.Color(0x222233),
        emissiveIntensity: 0.15,
        roughness: 0.4,
        metalness: 0.1,
      });
      const avatarPlane = new THREE.Mesh(geo, mat);
      avatarPlane.position.set(0, 1.1, 0.15); // Centered on body

      // Glow border behind the avatar
      const glowGeo = new THREE.PlaneGeometry(width + 0.25, height + 0.25);
      const glowMat = new THREE.MeshBasicMaterial({
        color: 0x4488ff,
        transparent: true,
        opacity: 0.25,
        side: THREE.DoubleSide,
      });
      const glowPlane = new THREE.Mesh(glowGeo, glowMat);
      glowPlane.position.set(0, 1.1, 0.1);

      // Hide the procedural model body parts (keep particles/effects)
      this.group.children.forEach(child => {
        if (child.type === 'Mesh' || child.type === 'Group') {
          child.visible = false;
        }
      });

      // Add avatar image to scene group
      this.group.add(glowPlane);
      this.group.add(avatarPlane);
      this._avatarPlane = avatarPlane;
      this._avatarGlow = glowPlane;

      console.log('✓ DALL-E avatar loaded as 3D billboard');
    }, undefined, (err) => {
      console.warn('Failed to load DALL-E avatar image:', err);
    });
  }

  _createUI() {
    const panel = document.createElement('div');
    panel.id = 'angel-chat-panel';
    panel.innerHTML = `
      <div class="angel-chat-header">
        <span>🪽 ${this.botName}</span>
        <span id="angel-voice-status" class="voice-status"></span>
      </div>
      <div id="angel-chat-log" class="angel-chat-log">
        <div class="angel-msg angel-msg-bot">I'm here with you in Veridian. Press <b>F</b> to open chat, hold <b>V</b> to talk to me with your voice. Type <b>follow</b>/<b>stay</b> or press <b>L</b>/<b>K</b>.</div>
      </div>
      <div class="angel-chat-input-row">
        <button id="angel-mic-btn" class="mic-btn" title="Hold V or click to talk">🎤</button>
        <input id="angel-chat-input" placeholder="Ask ${this.botName}... (or hold V to speak)" maxlength="400" />
        <button id="angel-chat-send">Send</button>
      </div>
    `;
    document.body.appendChild(panel);

    this.panel = panel;
    this.logEl = panel.querySelector('#angel-chat-log');
    this.inputEl = panel.querySelector('#angel-chat-input');
    this.micBtn = panel.querySelector('#angel-mic-btn');
    this.voiceStatusEl = panel.querySelector('#angel-voice-status');

    panel.querySelector('#angel-chat-send').addEventListener('click', () => this.sendFromInput());
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.sendFromInput();
    });

    // ─── Voice Chat Setup ───
    this.voiceChat = new VoiceChat({
      onTranscript: (text) => {
        this.addChat(text, 'user');
        this._handleVoiceMessage(text);
      },
      onListening: (active) => {
        this.micBtn.classList.toggle('listening', active);
        this.voiceStatusEl.textContent = active ? '🔴 Listening...' : '';
        // Auto-show panel when voice activates
        if (active && !this.panel.classList.contains('show')) {
          this.panel.classList.add('show');
        }
      },
      onError: (msg) => {
        this.addChat(`⚠ ${msg}`, 'system');
      }
    });

    this.voiceChat.bindInput(this.inputEl);

    // Push-to-talk with V key
    this.voiceChat.enablePushToTalk('v');

    // Mic button click toggle
    this.micBtn.addEventListener('click', () => {
      this.voiceChat.toggle();
    });

    window.addEventListener('keydown', (e) => {
      if (e.key.toLowerCase() === 'f') {
        // Don't toggle if typing in input
        if (isTextInputLikeElement(e.target)) return;
        this.panel.classList.toggle('show');
        if (this.panel.classList.contains('show')) this.inputEl.focus();
        return;
      }

      this.handleControlKeydown(e);
    });
  }

  async _handleVoiceMessage(text) {
    // Check for mode commands first
    const modeResult = this.handleModeCommand(text);
    if (modeResult.handled) {
      const modeReply = this._buildModeReply(modeResult);
      if (modeReply) {
        this.addChat(modeReply, 'bot');
        await this.speak(modeReply);
      }
      return;
    }

    // Get AI reply and speak it
    this.addChat('...', 'bot-thinking');
    const reply = await this.getReply(text);

    // Remove thinking indicator
    const thinking = this.logEl.querySelector('.angel-msg-bot-thinking');
    if (thinking) thinking.remove();

    this.addChat(reply, 'bot');
    await this.speak(reply);
  }

  _pickVoice() {
    if (!this.synth) return;
    const choose = () => {
      const voices = this.synth.getVoices();
      this.voice = voices.find(v => /female|samantha|aria|nova|alloy|en-us/i.test(v.name)) || voices[0] || null;
      this.voiceReady = !!this.voice;
    };
    choose();
    if (!this.voiceReady) window.speechSynthesis.onvoiceschanged = choose;
  }

  addChat(text, from = 'bot') {
    if (!this.logEl) return;
    const el = document.createElement('div');
    el.className = `angel-msg angel-msg-${from}`;
    el.textContent = text;
    this.logEl.appendChild(el);
    this.logEl.scrollTop = this.logEl.scrollHeight;
  }

  handleControlKeydown(event) {
    const key = (event?.key || '').toLowerCase();
    if (!key) return { handled: false };

    if (isTextInputLikeElement(event.target)) {
      return { handled: false, ignored: true };
    }

    if (key === CONTROL_KEYS.STAY) {
      event.preventDefault();
      return this._applyModeControl('stay');
    }

    if (key === CONTROL_KEYS.FOLLOW) {
      event.preventDefault();
      return this._applyModeControl('follow');
    }

    return { handled: false };
  }

  _applyModeControl(modeCommand) {
    const modeResult = this.handleModeCommand(modeCommand);
    if (!modeResult.handled) return modeResult;

    const modeReply = this._buildModeReply(modeResult);
    if (modeReply) this.addChat(modeReply, 'bot');

    return { ...modeResult, message: modeReply };
  }

  handleModeCommand(commandText) {
    return this.behaviorController.applyCommand(commandText);
  }

  getBehaviorMode() {
    return this.behaviorController.getMode();
  }

  _buildModeReply(modeResult) {
    if (!modeResult.handled) return null;

    if (modeResult.mode === ANGELA_BEHAVIOR_MODES.STAY) {
      return modeResult.changed
        ? 'I will stay right here until you ask me to follow again.'
        : 'I am already staying here with you.';
    }

    return modeResult.changed
      ? 'I am following you again.'
      : 'I am already following your path through Veridian.';
  }

  async sendFromInput() {
    const text = this.inputEl.value.trim();
    if (!text) return;
    this.inputEl.value = '';
    this.addChat(text, 'user');

    const modeResult = this.handleModeCommand(text);
    if (modeResult.handled) {
      const modeReply = this._buildModeReply(modeResult);
      if (modeReply) {
        this.addChat(modeReply, 'bot');
        await this.speak(modeReply);
      }
      return;
    }

    this.addChat('...', 'bot-thinking');
    const reply = await this.getReply(text);
    const thinking = this.logEl.querySelector('.angel-msg-bot-thinking');
    if (thinking) thinking.remove();
    this.addChat(reply, 'bot');
    await this.speak(reply);
  }

  async getReply(userMessage) {
    try {
      const res = await fetch('/api/angel/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          systemPrompt: this.systemPrompt,
        }),
      });
      if (!res.ok) throw new Error(`chat failed ${res.status}`);
      const data = await res.json();
      return data.reply || 'I am here with you.';
    } catch {
      // Local fallback using bot name
      const low = userMessage.toLowerCase();
      if (low.includes('help')) return 'Tell me what you want to build next, and I will help you step by step.';
      if (low.includes('where')) return 'I am right beside you, following your path through Veridian.';
      if (low.includes('who are you') || low.includes('your name')) return `I am ${this.botName}, your personal companion in Veridian.`;
      return `I hear you. I am ${this.botName}, with you in this world.`;
    }
  }

  _startLipSync() {
    this.lastTalkAt = performance.now();
    this.lipSyncController.start();
  }

  _stopLipSync() {
    this.lipSyncController.stop();
  }

  _resetLipSync() {
    this.lipSyncController.reset();
    this.mouth.scale.y = 1;
  }

  async speak(text) {
    // Try ElevenLabs via server endpoint first
    try {
      const res = await fetch('/api/angel/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (res.ok && (res.headers.get('content-type') || '').includes('audio')) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        await this._playAudioWithLipSync(audio);
        URL.revokeObjectURL(url);
        return;
      }
    } catch {}

    // Browser TTS fallback
    if (!this.synth) return;
    const utter = new SpeechSynthesisUtterance(text);
    if (this.voice) utter.voice = this.voice;
    utter.rate = 0.98;
    utter.pitch = 1.07;
    utter.onstart = () => {
      this._startLipSync();
    };
    utter.onend = () => {
      this._stopLipSync();
    };
    utter.onerror = () => {
      this._resetLipSync();
    };
    this.synth.cancel();
    this.synth.speak(utter);
  }

  async _playAudioWithLipSync(audio) {
    this._startLipSync();
    audio.onended = () => {
      this._stopLipSync();
    };
    audio.onerror = () => {
      this._resetLipSync();
    };

    try {
      await audio.play();
    } catch (error) {
      this._resetLipSync();
      throw error;
    }
  }

  update(timeSec) {
    const previousTime = this._lastUpdateTimeSec;
    const deltaSec = previousTime == null ? 0 : Math.max(0, Math.min(0.1, timeSec - previousTime));
    this._lastUpdateTimeSec = timeSec;

    if (this.behaviorController.shouldFollow()) {
      // Follow camera
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion).setY(0).normalize();
      const target = this.camera.position.clone()
        .add(forward.multiplyScalar(this.followDistance))
        .add(new THREE.Vector3(0, this.followHeight, 0));

      this.group.position.lerp(target, 0.14);

      // Idle float while moving with player
      this.group.position.y += Math.sin(timeSec * 1.8) * 0.0015;
    }

    // Look at camera (soft)
    const lookTarget = this.camera.position.clone();
    lookTarget.y = this.group.position.y + 1.5;
    this.group.lookAt(lookTarget);

    // Wing flutter
    if (this.leftWing && this.rightWing) {
      const flap = Math.sin(timeSec * 2.6) * 0.08;
      this.leftWing.rotation.y = 0.35 + flap;
      this.rightWing.rotation.y = -0.35 - flap;
    }

    // Halo shimmer
    if (this.halo) {
      const glow = 0.55 + (Math.sin(timeSec * 2.0) + 1) * 0.15;
      this.halo.material.emissiveIntensity = glow;
      this.halo.rotation.z += 0.004;
    }

    // Particle rotation
    if (this.particles) {
      this.particles.rotation.y = timeSec * 0.15;
    }

    // Gem pulse
    const gem = this.group.getObjectByName('companionGem');
    if (gem) {
      gem.material.emissiveIntensity = 0.7 + Math.sin(timeSec * 3) * 0.3;
    }

    // Lip movement (speech lifecycle + smoothing)
    const mouthScaleY = this.lipSyncController.update(deltaSec, timeSec);
    if (this.mouth) this.mouth.scale.y = mouthScaleY;

    // DALL-E avatar billboard animation
    if (this._avatarPlane) {
      // Subtle float
      this._avatarPlane.position.y = 1.1 + Math.sin(timeSec * 1.5) * 0.04;
      // Glow pulse
      if (this._avatarGlow) {
        this._avatarGlow.material.opacity = 0.2 + Math.sin(timeSec * 2.0) * 0.08;
      }
    }
  }
}
